import * as React from 'react';

import { SpaceContainerStyles } from '~src/spacesPlatform/style/SpaceContainerStyles';

import { DetailPanel } from './DetailPanel';
import { ListDetailStyles } from './ListDetailStyles';
import { ListDetailToolbar } from './ListDetailToolbar';
import { ListPanel } from './ListPanel';
import { ListDetailLayoutProps, ListDetailState } from './types';

/**
 * ListDetailLayout Component
 *
 * A reusable layout component that provides a list-detail interface with:
 * - List panel (left side): Sizes defined by ListDetailStyles
 * - Detail panel (right side)
 * - Optional collapsible list panel
 * - Smooth transitions and animations
 *
 * @example
 * ```tsx
 * <ListDetailLayout
 *   renderList={() => <ProfileList profiles={profiles} />}
 *   renderDetail={() => <ProfileDetails profile={selectedProfile} />}
 *   collapsible={false}
 * />
 * ```
 */
export class ListDetailLayout extends React.PureComponent<ListDetailLayoutProps, ListDetailState> {
  public static readonly defaultProps: Partial<ListDetailLayoutProps> = {
    collapsible: false,
    initiallyCollapsed: false,
  };

  constructor(props: ListDetailLayoutProps) {
    super(props);

    this.state = {
      collapsed: props.initiallyCollapsed || false,
    };
  }

  public componentDidUpdate(prevProps: ListDetailLayoutProps, prevState: ListDetailState): void {
    // Notify parent of collapse changes
    if (prevState.collapsed !== this.state.collapsed && this.props.onCollapseChange) {
      this.props.onCollapseChange(this.state.collapsed);
    }
  }

  public render(): JSX.Element {
    const {
      renderList,
      renderDetail,
      mastheadElement,
      toolbarProps,
      testId = 'list-detail-layout',
      className
    } = this.props;

    const { collapsed } = this.state;

    return (
      <div
        data-tb-test-id={testId}
        className={`${ListDetailStyles.LayoutContainer} ${className || ''}`}
      >
        {mastheadElement && (
          <div className={SpaceContainerStyles.MastheadArea}>
            {mastheadElement}
          </div>
        )}

        {toolbarProps && (
          <ListDetailToolbar
            {...toolbarProps}
            testId={`${testId}-toolbar`}
          />
        )}

        <div className={ListDetailStyles.PanelsContainer}>
          <ListPanel
            collapsed={collapsed}
            testId={`${testId}-list`}
          >
            {renderList()}
          </ListPanel>

          <DetailPanel
            leftMargin={0}
            testId={`${testId}-detail`}
          >
            {renderDetail()}
          </DetailPanel>
        </div>
      </div>
    );
  }
}
